import React, { useState, useMemo, useEffect } from 'react'
import {
  Box,
  Button,
  Stack,
  Typography,
  Divider,
  TextField,
} from '@mui/material'
import { PhoneNumberInput } from '../../atom/inputs/PhoneNumberInput'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CheckIcon from '@mui/icons-material/Check'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import AddIcon from '@mui/icons-material/Add'
import deleteIcon from 'shared/assets/icons/Delete.svg'
import { ProfileSlice } from 'shared/redux/profile/ProfileTypes'
import { Grant } from 'shared/redux/product/ProductTypes'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../redux/storeExports'
import { PostProfile, triggerTodoListAction, ProfileSelectors, MarkGettingStartedCompleted, FetchProfile } from 'shared/redux/profile/ProfileSlice'
import { fromProfileFields, asProfilePost } from 'shared/schemas/ProfileForm'
import { CityInput } from '../../atom/inputs/CityInput'
import { StateInput } from '../../atom/inputs/StateInput'
import OnboardingTaskModal from './OnboardingTaskModal'
import { postFamilyStatus, selectFamilyStatus } from 'shared/redux/product/ProductSlice'
import { isValidPhoneNumber } from 'libphonenumber-js/max'
import { TodoMenu } from '../DashboardTodoList/DashboardTodoMenu'
import { TODO_ITEMS } from '../DashboardTodoList/DashboardTodoConstants'


interface MultiStepProfileCompletionModalProps {
  open: boolean
  setOpen: () => void
  profile: ProfileSlice
  grants: Grant | null
  brokerListLength: number
  shouldShowProfileCompletionAlert: (profile: ProfileSlice) => boolean
}

const compactDropdownSx = {
  '& .MuiAutocomplete-listbox': {
    maxHeight: 200,
    '& .MuiAutocomplete-option': {
      fontFamily: 'InterRegular',
      fontSize: '13px',
      lineHeight: '18px',
      py: '4px',
      minHeight: 'auto',
    },
  },
  '& .MuiAutocomplete-noOptions': {
    fontFamily: 'InterRegular',
    fontSize: '13px',
    lineHeight: '18px',
    py: '8px',
    minHeight: 'auto',
  },
}

const MultiStepProfileCompletionModal = (props: MultiStepProfileCompletionModalProps) => {
  const { profile, grants } = props
  const navigate = useNavigate()
  const [activeSubModal, setActiveSubModal] = useState<string | null>(null)
  const isBusinessUser = grants?.grant_source === 'corporate_grant'
  const isFamilyUser = grants?.sku?.split('.')[2] === 'fam'
  const familyStatus = useAppSelector(selectFamilyStatus)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteError, setInviteError] = useState('')
  const [inviteLoading, setInviteLoading] = useState(false)
  const isPeopleFindersCompleted = profile.metadata?.todo_actions?.some((a) => a.action_category === 'broker' && a.broker_name === 'peoplefinders' && (a.action === 'confirmator' || a.action === 'later' || a.action === 'removal'))
  const isInteliusCompleted = profile.metadata?.todo_actions?.some((a) => a.action_category === 'broker' && a.broker_name === 'intelius' && (a.action === 'confirmator' || a.action === 'later' || a.action === 'removal'))
  const isDataCommAcknowledged = !!profile.metadata?.todo_actions?.some((a) => a.action_category === 'data_comm_acknowledgement' && (a.action === 'acknowledge' || a.action === 'later'))

  const brokersRequireRemoval = useAppSelector(ProfileSelectors.selectBrokersRequireRemoval)
  const isPeopleFindersNotNeeded = !(Array.isArray(brokersRequireRemoval) && brokersRequireRemoval.includes('peoplefinders')) && !isPeopleFindersCompleted;
  const isInteliusNotNeeded = !(Array.isArray(brokersRequireRemoval) && brokersRequireRemoval.includes('intelius')) && !isInteliusCompleted;

  const removalSubSteps = [
    {
      id: 'peopleFinders',
      label: 'PeopleFinders',
      description: 'Remove your data from PeopleFinders',
      isCompleted: isPeopleFindersCompleted,
      isNotNeeded: isPeopleFindersNotNeeded,
      buttonLabel: isPeopleFindersNotNeeded ? 'No Action Needed' : 'View',
    },
    {
      id: 'intelius',
      label: 'Intelius',
      description: 'Remove your data from Intelius',
      isCompleted: isInteliusCompleted,
      isNotNeeded: isInteliusNotNeeded,
      buttonLabel: isInteliusNotNeeded ? 'No Action Needed' : 'View',
    },
    {
      id: 'dataBrokerComm',
      label: 'Data Broker Communications',
      description: 'Grant email authorization for removals',
      isCompleted: isDataCommAcknowledged,
      isNotNeeded: false,
      buttonLabel: 'View',
    },
  ].filter(step => !(isBusinessUser && (step.id === 'intelius' || step.id === 'peopleFinders')))

  const completedRemovalSteps = removalSubSteps.filter(s => s.isCompleted || s.isNotNeeded).length
  const totalRemovalSteps = removalSubSteps.length
  const removalProgress = totalRemovalSteps > 0 ? (completedRemovalSteps / totalRemovalSteps) * 100 : 0

  // const isRemovalCompleted = removalSubSteps.every(s => s.isCompleted)

  // Sub-steps for the "Improve Scan" flow
  const improveScanSubSteps = [
    {
      id: 'middleName',
      label: 'Add middle name',
      description: 'Provide a middle name to improve matching',
      isCompleted: !!profile?.names?.middle?.[0],
    },
    {
      id: 'streetAddress',
      label: 'Add street address',
      description: 'Provide your street address',
      isCompleted: !!profile?.address && profile.address.length > 0 && !!profile.address[0].street1,
    },
    {
      id: 'phoneNumber',
      label: 'Add phone number',
      description: 'Provide a phone number for better matches',
      isCompleted: !!profile?.phones && !!profile.phones[0]?.number,
    },
  ]

  const showImproveScan = !profile?.phones?.[0]?.number || !profile?.address?.[0]?.street1
  // const hasAdditionalAddress = (profile?.address?.length || 0) > 1

  const stepsData = useMemo(() => {
    const list = []

    // if (showImproveScan) {
    list.push({
      id: 'improveScan',
      label: 'Improve Your Scan Results',
      description: 'Add details to get the most from your scan',
      isCompleted:
        !!profile?.address?.[0]?.street1 &&
        !!profile?.address?.[0]?.zip &&
        !!profile?.phones?.[0]?.number,
    })
    // } 
    // else {
    //   list.push({
    //     id: 'additionalAddresses',
    //     label: 'Add additional addresses',
    //     description: 'Improve match accuracy with more addresses',
    //     isCompleted: hasAdditionalAddress,
    //   })
    // }

    list.push({
      id: 'authLetter',
      label: 'Data Broker Email Authorization',
      description: 'Allow us to request removals on your behalf',
      isCompleted: !!profile?.auth_letter_s3_key,
      hasInfoBadge: true,
      infoLabel: 'Authorization Info',
    })

    const isRemovalTasksCompleted =
      (isPeopleFindersCompleted || isPeopleFindersNotNeeded) &&
      (isBusinessUser || isInteliusCompleted || isInteliusNotNeeded) &&
      isDataCommAcknowledged
    if (isFamilyUser) {
      list.push({
        id: 'addPlusOne',
        label: 'Add Plus One',
        description: 'You can add a plus one user',
        isCompleted: !!familyStatus.email || ['accepted', 'invited', 'queued'].includes(familyStatus.invite_status),
      })
    }

    list.push({
      id: 'removalTasks',
      label: 'Complete Removal Tasks',
      description: 'Finalize your data removal process',
      isCompleted: isRemovalTasksCompleted,
    })

    const finalSteps = (isBusinessUser || isFamilyUser || grants?.grant_source === 'family_grant')
      ? list.filter(step => !step.isCompleted || ['removalTasks', 'addPlusOne'].includes(step.id))
      : list
    return {
      allSteps: list,
      displaySteps: finalSteps
    }
  }, [
    profile,
    // showImproveScan,
    isPeopleFindersCompleted,
    isBusinessUser,
    isInteliusCompleted,
    isDataCommAcknowledged,
    isInteliusNotNeeded,
    isPeopleFindersNotNeeded,
    isFamilyUser,
    familyStatus,
    grants
  ])

  const { allSteps, displaySteps } = stepsData
  const completedStepsCount = displaySteps.filter((step) => step.isCompleted).length
  const totalSteps = displaySteps.length
  const progress = totalSteps > 0 ? (completedStepsCount / totalSteps) * 100 : 100
  const isCompulsoryStepsCompleted = allSteps.every((step) => step.isCompleted)

  const [todoAnchorEl, setTodoAnchorEl] = useState<HTMLElement | null>(null)
  const [menuWidth, setMenuWidth] = useState<number>()
  const [activeTodoItemId, setActiveTodoItemId] = useState<number | null>(null)

  const dispatch = useAppDispatch()
  const lastActiveTodoItemIdRef = React.useRef<number | null>(null)

  useEffect(() => {
    if (activeTodoItemId !== null) {
      lastActiveTodoItemIdRef.current = activeTodoItemId
    }
  }, [activeTodoItemId])

  useEffect(() => {
    const isRemovalCompleted =
      (isPeopleFindersCompleted || isPeopleFindersNotNeeded) &&
      (isBusinessUser || isInteliusCompleted || isInteliusNotNeeded) &&
      isDataCommAcknowledged
    if (activeSubModal === 'removalTasks' && isRemovalCompleted) {
      handleSubModalClose()
    }
  }, [
    activeSubModal,
    isPeopleFindersCompleted,
    isBusinessUser,
    isInteliusCompleted,
    isDataCommAcknowledged,
    isPeopleFindersNotNeeded,
    isInteliusNotNeeded,
  ])
  const [loading, setLoading] = useState(false)

  const [inputValue, setInputValue] = useState(profile?.phones?.[0]?.number || '')
  const [maidenName, setMaidenName] = useState(profile?.additional_information?.maiden_name?.[0] || '')
  const [altName, setAltName] = useState(profile?.additional_information?.additional_first_name?.[0] || '')
  const [addressValue, setAddressValue] = useState({
    street1: profile?.address?.[0]?.street1 || '',
    city: profile?.address?.[0]?.city || '',
    state: profile?.address?.[0]?.state || '',
    zip: profile?.address?.[0]?.zip || '',
  })
  const [additionalAddresses, setAdditionalAddresses] = useState<any[]>(profile?.address?.slice(1) || [])
  const [phoneError, setPhoneError] = useState(false)
  const [zipError, setZipError] = useState(false)
  const [streetError, setStreetError] = useState(false)
  const [cityError, setCityError] = useState(false)
  const [stateError, setStateError] = useState(false)
  const [maidenNameError, setMaidenNameError] = useState(false)
  const [altNameError, setAltNameError] = useState(false)

  const [newAddressValue, setNewAddressValue] = useState({
    street1: '',
    city: '',
    state: '',
    zip: '',
  })
  const [newStreetError, setNewStreetError] = useState(false)
  const [newCityError, setNewCityError] = useState(false)
  const [newStateError, setNewStateError] = useState(false)
  const [newZipError, setNewZipError] = useState(false)
  const [additionalAddressErrors, setAdditionalAddressErrors] = useState<any[]>([])


  const validateZip = (zip: string) => {
    return !zip || /^\d{5}$/.test(zip)
  }

  const validateState = (state: string) => {
    const states = [
      'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DC', 'DE', 'FL', 'GA', 'HI',
      'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN',
      'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH',
      'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA',
      'WV', 'WI', 'WY', 'AS', 'GU', 'MP', 'PR', 'UM', 'VI'
    ]
    return states.includes(state.trim().toUpperCase())
  }

  const isPhoneValid = useMemo(() => {
    return isValidPhoneNumber(inputValue)
  }, [inputValue])

  const isMiddleNameValid = useMemo(() => {
    return inputValue.trim().length > 0
  }, [inputValue])

  const handleNavigate = (step: any) => {
    if (step.id === 'authLetter') {
      navigate('/authorization')
      props.setOpen()
      return
    }
    // When opening the composite improveScan modal, prefill values from profile
    if (step.id === 'improveScan') {
      setInputValue(profile?.phones?.[0]?.number || '')
      setAddressValue({
        street1: profile?.address?.[0]?.street1 || '',
        city: profile?.address?.[0]?.city || '',
        state: profile?.address?.[0]?.state || '',
        zip: profile?.address?.[0]?.zip || '',
      })
      setMaidenName(profile?.additional_information?.maiden_name?.[0] || '')
      setAltName(profile?.additional_information?.additional_first_name?.[0] || '')
      setAdditionalAddresses(profile?.address?.slice(1) || [])
    }
    if (step.id === 'additionalAddresses') {
      setNewAddressValue({
        street1: '',
        city: '',
        state: '',
        zip: '',
      })
      setNewStreetError(false)
      setNewCityError(false)
      setNewStateError(false)
      setNewZipError(false)
      setInviteEmail('')
      setInviteError('')
    }
    if (step.id === 'addPlusOne') {
      setActiveSubModal('addPlusOne')
      setInviteEmail('')
      setInviteError('')
      return
    }
    if (step.id === 'streetAddress') {
      setNewAddressValue({
        street1: '',
        city: '',
        state: '',
        zip: '',
      })
      setNewStreetError(false)
      setNewCityError(false)
      setNewStateError(false)
      setNewZipError(false)
    }
    setActiveSubModal(step.id)
    if (step.id === 'middleName') setInputValue(profile?.names?.middle?.[0] || '')
    if (step.id === 'phoneNumber') setInputValue(profile?.phones?.[0]?.number || '')
    setPhoneError(false)
    setZipError(false)
  }

  const handleSave = async () => {
    setLoading(true)
    if (activeSubModal === 'addPlusOne') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!inviteEmail.trim()) {
        setInviteError('Email is required')
        setLoading(false)
        return
      }
      if (!emailRegex.test(inviteEmail.trim())) {
        setInviteError('Invalid email format')
        setLoading(false)
        return
      }

      try {
        setInviteLoading(true)
        await dispatch(postFamilyStatus(inviteEmail.trim())).unwrap()
        handleSubModalClose()
      } catch (error) {
        setInviteError('Failed to send invite')
      } finally {
        setInviteLoading(false)
        setLoading(false)
      }
      return
    }

    try {
      const schema = fromProfileFields(profile as any)
      // Save improveScan composite form
      if (activeSubModal === 'improveScan') {
        // Strict individual validation for address fields
        const street = (addressValue.street1 || '').trim()
        const city = (addressValue.city || '').trim()
        const state = (addressValue.state || '').trim()
        const zip = (addressValue.zip || '').trim()

        setStreetError(!street)
        setCityError(!city || city.endsWith('-'))
        setStateError(!state || !validateState(state))
        setZipError(!zip || !/^\d{5}$/.test(zip))
        setPhoneError(!isValidPhoneNumber(inputValue))
        setMaidenNameError(/[ -]+$/.test(maidenName || ''))
        setAltNameError(/[ -]+$/.test(altName || ''))

        const newAdditionalErrors = additionalAddresses.map(addr => {
          if (!addr) return { street1: true, city: true, state: true, zip: true }
          return {
            street1: !(addr.street1 || addr.streetAddress || '').trim(),
            city: !(addr.city || '').trim() || (addr.city || '').trim().endsWith('-'),
            state: !(addr.state || '').trim() || !validateState((addr.state || '').trim()),
            zip: !(addr.zip || '').trim() || !/^\d{5}$/.test((addr.zip || '').trim())
          }
        })
        setAdditionalAddressErrors(newAdditionalErrors)

        if (
          !street || !city || city.endsWith('-') || !state || !validateState(state) || !zip || !/^\d{5}$/.test(zip) ||
          (!isValidPhoneNumber(inputValue)) ||
          /[ -]+$/.test(maidenName || '') || /[ -]+$/.test(altName || '') ||
          newAdditionalErrors.some(err => err.street1 || err.city || err.state || err.zip)
        ) {
          setLoading(false)
          return
        }

        setPhoneError(false)

        // Check if any values actually changed from the profile
        const originalPhone = profile?.phones?.[0]?.number || ''
        const originalStreet = profile?.address?.[0]?.street1 || ''
        const originalCity = profile?.address?.[0]?.city || ''
        const originalState = profile?.address?.[0]?.state || ''
        const originalZip = profile?.address?.[0]?.zip || ''
        const originalMaidenName = profile?.additional_information?.maiden_name?.[0] || ''
        const originalAltName = profile?.additional_information?.additional_first_name?.[0] || ''

        const phoneChanged = (inputValue || '').trim() !== originalPhone
        const addressChanged =
          (addressValue.street1 || '').trim() !== originalStreet ||
          (addressValue.city || '').trim() !== originalCity ||
          (addressValue.state || '').trim() !== originalState ||
          (addressValue.zip || '').trim() !== originalZip
        const maidenNameChanged = (maidenName || '').trim() !== originalMaidenName
        const altNameChanged = (altName || '').trim() !== originalAltName

        const originalAdditional = profile?.address?.slice(1) || []
        const additionalChanged =
          additionalAddresses.length !== originalAdditional.length ||
          additionalAddresses.some((a, i) => {
            const orig = originalAdditional[i]
            if (!a && !orig) return false
            if (!a || !orig) return true
            return (
              (a.streetAddress || a.street1 || '').trim() !== (orig?.street1 || '') ||
              (a.city || '').trim() !== (orig?.city || '') ||
              (a.state || '').trim() !== (orig?.state || '') ||
              (a.zip || '').trim() !== (orig?.zip || '')
            )
          })

        if (!phoneChanged && !addressChanged && !maidenNameChanged && !altNameChanged && !additionalChanged) {
          handleSubModalClose()
          setLoading(false)
          return
        }

        // Update schema with current form values
        if (inputValue) {
          const phoneWithoutSpaces3 = inputValue.replace(/\s+/g, '')
          schema.phoneNumber = [{ value: phoneWithoutSpaces3 }]
        }

        // Update the FIRST address
        schema.address[0] = {
          streetAddress: addressValue.street1.trim(),
          city: addressValue.city.trim(),
          state: addressValue.state as any,
          zip: addressValue.zip.trim(),
        }

        // Add additional addresses from local state
        const localAdditional = additionalAddresses
          .filter(a => !!a)
          .map(a => ({
            streetAddress: (a.streetAddress || a.street1 || '').trim(),
            city: (a.city || '').trim(),
            state: (a.state || '').trim() as any,
            zip: (a.zip || '').trim(),
          }))

        schema.address = [schema.address[0], ...localAdditional]

        if (maidenName) schema.otherMaidenName = [{ value: maidenName.trim() }]
        if (altName) schema.otherFirstName = [{ value: altName.trim() }]

        const payload = {
          ...asProfilePost(schema),
          isOnBoardingRequest: false,
          isGettingStartedRequest: true
        }

        //@ts-ignore
        await dispatch(PostProfile(payload)).unwrap()
        handleSubModalClose()
        return
      }


      const updatedProfile = JSON.parse(JSON.stringify(profile))
      if (activeSubModal === 'middleName') {
        updatedProfile.names.middle = [inputValue.trim()]
      } else if (activeSubModal === 'phoneNumber') {
        if (!isValidPhoneNumber(inputValue)) {
          setPhoneError(true)
          setLoading(false)
          return
        }
        setPhoneError(false)
        const phoneWithoutSpaces2 = inputValue.replace(/\s+/g, '')
        updatedProfile.phones = [
          { number: phoneWithoutSpaces2, validated: false }
        ]
      } else if (activeSubModal === 'streetAddress' || activeSubModal === 'additionalAddresses') {
        const isStreetValid = newAddressValue.street1.trim().length > 0
        const isCityValid = newAddressValue.city.trim().length > 0 && !newAddressValue.city.trim().endsWith('-')
        const isStateValid = validateState(newAddressValue.state)
        const isZipValid = validateZip(newAddressValue.zip) && newAddressValue.zip.trim().length === 5

        setNewStreetError(!isStreetValid)
        setNewCityError(!isCityValid)
        setNewStateError(!isStateValid)
        setNewZipError(!isZipValid)

        if (!isStreetValid || !isCityValid || !isStateValid || !isZipValid) {
          setLoading(false)
          return
        }

        const newAddress = {
          street1: newAddressValue.street1.trim(),
          city: newAddressValue.city.trim(),
          state: newAddressValue.state as any,
          zip: newAddressValue.zip.trim(),
        }

        setAdditionalAddresses([...additionalAddresses, newAddress])

        if (!showImproveScan) {
          // If we came from the main list, we need to save now
          schema.address = [schema.address[0], ...additionalAddresses, newAddress]
          const payload = {
            ...asProfilePost(schema),
            isOnBoardingRequest: false,
            isGettingStartedRequest: true
          }
          //@ts-ignore
          await dispatch(PostProfile(payload)).unwrap()
          handleSubModalClose()
        } else {
          // If we came from improveScan sub-page, just go back
          setActiveSubModal('improveScan')
        }
        setLoading(false)
        return
      }
      //@ts-ignore
      const payload = activeSubModal === 'phoneNumber' || activeSubModal === 'middleName'
        ? { ...updatedProfile, isOnBoardingRequest: false, isGettingStartedRequest: true }
        : { ...asProfilePost(schema), isOnBoardingRequest: false, isGettingStartedRequest: true }

      // Ensure phone numbers have + prefix if needed
      if (payload.phones) {
        payload.phones = payload.phones.map((p: any) => ({
          ...p,
          number: p.number.startsWith('+') ? p.number : (p.number.length === 10 ? `+1${p.number}` : `+${p.number}`)
        }))
      }

      await dispatch(PostProfile(payload)).unwrap()

      // Update local state for additional addresses so the main modal is in sync
      if (activeSubModal === 'streetAddress') {
        setAdditionalAddresses(updatedProfile.address.slice(1))
      }

      if (!showImproveScan) {
        handleSubModalClose()
      } else {
        // Go back to improveScan if it was part of it
        setActiveSubModal('improveScan')
      }
    } catch (err) {
      console.error('Failed to save profile', err)
    } finally {
      setLoading(false)
    }
  }

  const handleSubModalClose = () => {
    setActiveSubModal(null)
    setInputValue('')
    setMaidenName('')
    setAltName('')
    setInviteEmail('')
    setInviteError('')
    setAddressValue({
      street1: '',
      city: '',
      state: '',
      zip: '',
    })
    setAdditionalAddresses([])
    setPhoneError(false)
    setZipError(false)
    setMaidenNameError(false)
    setAltNameError(false)
  }

  const handleBackToImproveScan = () => {
    setActiveSubModal('improveScan')
    setStreetError(false)
    setCityError(false)
    setStateError(false)
    setZipError(false)
    setAddressValue({
      street1: profile?.address?.[0]?.street1 || '',
      city: profile?.address?.[0]?.city || '',
      state: profile?.address?.[0]?.state || '',
      zip: profile?.address?.[0]?.zip || '',
    })
    setMaidenNameError(false)
    setAltNameError(false)
  }

  const handleDeleteAddress = (index: number) => {
    const newList = [...additionalAddresses]
    newList.splice(index, 1)
    setAdditionalAddresses(newList)
  }



  const handleRemovalAction = (event: React.MouseEvent<HTMLElement>, stepId: string) => {
    if (stepId === 'peopleFinders') {
      setMenuWidth(500)
      setTodoAnchorEl(event.currentTarget)
      setActiveTodoItemId(1)
    } else if (stepId === 'intelius') {
      setMenuWidth(500)
      setTodoAnchorEl(event.currentTarget)
      setActiveTodoItemId(2)
    } else if (stepId === 'dataBrokerComm') {
      setMenuWidth(500)
      setTodoAnchorEl(event.currentTarget)
      setActiveTodoItemId(3)
    }
  }

  const handleCloseTodoMenu = () => {
    setTodoAnchorEl(null)
    setActiveTodoItemId(null)
  }

  const handleFinish = async () => {
    setLoading(true)
    try {
      await dispatch(MarkGettingStartedCompleted()).unwrap()
      await dispatch(FetchProfile()).unwrap()
      window.location.reload();
      props.setOpen()
    } catch (err) {
      console.error('Failed to finish getting started', err)
    } finally {
      setLoading(false)
    }
  }

  const handleClose = (event?: any, reason?: string) => {
    if (reason === 'backdropClick') return
    if (activeSubModal !== null) {
      handleSubModalClose()
      return
    }

    if (isCompulsoryStepsCompleted) {
      props.setOpen()
    }
  }

  const renderMainStep = (step: any, index: number) => {
    const currentRenderSteps = activeSubModal === 'improveScan' ? improveScanSubSteps : displaySteps
    const isNextStep = index === currentRenderSteps.findIndex((s: any) => !s.isCompleted)
    return (
      <Box
        sx={{
          p: 2.5,
          pl: '24px',
          pr: '24px',
          pt: '24px',
          pb: '24px',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          '&:hover': { background: '#f1f3f4' },
        }}>
        <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
          {step.isCompleted ? (
            <CheckCircleIcon sx={{ color: '#1a73e8' }} />
          ) : (
            <RadioButtonUncheckedIcon sx={{ color: '#dadce0' }} />
          )}
          <Box>
            <Typography variant="subtitle1" sx={{
              fontFamily: 'InterMedium', fontWeight: 400,
              color: step.isCompleted ? '#3c4043' : '#202124',
              mb: 0.5,
              fontSize: '16px',
              lineHeight: '22px',
              letterSpacing: '0px',
              verticalAlign: 'middle',
            }}>
              {step.label}
            </Typography>
            <Typography variant="body2" sx={{ mt: '8px', color: '#3C4252', fontSize: '16px', fontFamily: 'InterMedium', fontWeight: 400 }}>
              {step.description}
            </Typography>
            {step.hasInfoBadge && !step.isCompleted && (
              <Box sx={{ width: '160px', height: '25px', display: 'inline-flex', alignItems: 'center', background: '#e8f0fe', color: '#1967d2', px: 1, py: 0.25, borderRadius: '6px', mt: 1, fontSize: '11px', fontFamily: 'InterMedium', fontWeight: 400 }}>
                <Box component="span" sx={{ mr: 0.5, ml: 1, width: 14, height: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1967d2', color: '#fff', borderRadius: '50%', fontSize: '10px' }}>i</Box>
                <span style={{ fontSize: '14px', marginLeft: '5px' }}>{step.infoLabel}</span>
              </Box>
            )}
          </Box>
        </Stack>
        <Box sx={{ ml: 2, mt: 0.5 }}>
          {step.isCompleted ? (
            <Box sx={{ color: '#3C4252', fontSize: '14px', fontFamily: 'InterMedium', fontWeight: 400, display: 'flex', alignItems: 'center', gap: 0.5, p: '6px', background: '#F4F4F4', borderRadius: '8px' }}>
              <CheckIcon sx={{ fontSize: '18px' }} /> Completed
            </Box>
          ) : (
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleNavigate(step)}
              endIcon={<ChevronRightIcon />}
              disabled={!isNextStep}
              sx={{
                textTransform: 'none', borderRadius: '8px', fontFamily: 'InterMedium', fontWeight: 400, height: '40px', width: '93px', fontSize: '14px',
                color: isNextStep ? '#fff' : '#3c4043',
                background: isNextStep ? 'linear-gradient(180deg, #1E89ED 0%, #0A71D9 100%)' : '#F4F4F4',
                border: 'none',
                '&:hover': { background: isNextStep ? 'linear-gradient(180deg, #1E89ED 0%, #0A71D9 100%)' : '#ececec', border: 'none', opacity: 0.9 },
                '&.Mui-disabled': {
                  background: '#E5E5E5',
                  color: '#A3A3A3',
                  border: 'none',
                }
              }}>
              {isNextStep ? 'Start' : 'View'}
            </Button>
          )}
        </Box>
      </Box>
    )
  }

  const renderRemovalStep = (step: any) => {
    return (
      <Box sx={{ p: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={2} sx={{ flex: 1, alignItems: 'center' }}>
          {step.isCompleted || step.isNotNeeded ? (
            <CheckCircleIcon sx={{ color: '#1a73e8' }} />
          ) : (
            <RadioButtonUncheckedIcon sx={{ color: '#dadce0' }} />
          )}
          <Box>
            <Typography variant="subtitle1" sx={{
              fontFamily: 'InterMedium', fontWeight: 400,
              fontSize: '18px',
              color: '#1D2333',
              lineHeight: '22px',
              letterSpacing: '0px',
              verticalAlign: 'middle'
            }}>
              {step.label}
            </Typography>
            <Typography variant="body2" sx={{ color: '#3C4252', fontSize: '14px', fontFamily: 'InterMedium', fontWeight: 400 }}>
              {step.description}
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ ml: 2 }}>
          {step.isCompleted ? (
            <Box sx={{ color: '#3C4252', fontSize: '14px', fontFamily: 'InterMedium', fontWeight: 400, display: 'flex', alignItems: 'center', gap: 0.5, p: '6px 12px', background: '#F4F4F4', borderRadius: '8px' }}>
              <CheckIcon sx={{ fontSize: '16px' }} /> Completed
            </Box>
          ) : step.isNotNeeded ? (
            <Box sx={{ color: '#3C4252', fontSize: '14px', fontFamily: 'InterMedium', fontWeight: 400, display: 'flex', alignItems: 'center', gap: 0.5, p: '6px 12px', background: '#F4F4F4', borderRadius: '8px' }}>
              No Action Needed
            </Box>
          ) : (
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => handleRemovalAction(e, step.id)}
              endIcon={<ChevronRightIcon />}
              disabled={false}
              sx={{
                textTransform: 'none', borderRadius: '8px', fontFamily: 'InterMedium', fontWeight: 400, height: '36px',
                background: step.buttonLabel === 'Start' ? '#1a73e8' : '#F4F4F4',
                color: step.buttonLabel === 'Start' ? '#fff' : '#3c4043',
                border: 'none',
                '&:hover': { background: step.buttonLabel === 'Start' ? '#1557b0' : '#ececec', border: 'none' },
                '&.Mui-disabled': {
                  background: '#E5E5E5',
                  color: '#A3A3A3',
                  border: 'none',
                }
              }}>
              {step.buttonLabel}
            </Button>
          )}
        </Box>
      </Box>
    )
  }

  return (
    <>
      <OnboardingTaskModal
        open={props.open}
        onClose={handleClose}
        title="Getting Started"
        subtitle="Complete these steps to begin your data removal process"
        completedCount={completedStepsCount}
        totalSteps={totalSteps}
        progress={progress}
        footerButtonText="Finish"
        onFooterButtonClick={handleFinish}
        footerButtonDisabled={!isCompulsoryStepsCompleted}
        hideCloseIcon={true}
      >
        <Box
          sx={{
            background: '#ffffff',
            borderRadius: '16px',
            border: '1px solid #DDDEE4',
            overflow: 'hidden',
          }}>
          {displaySteps.map((step, index) => (
            <React.Fragment key={step.id || index}>
              {renderMainStep(step, index)}
              {index < displaySteps.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </Box>
      </OnboardingTaskModal>


      <OnboardingTaskModal
        open={activeSubModal === 'addPlusOne'}
        onClose={handleSubModalClose}
        title="Plus One Management"
        subtitle="Invite your plus one to your account."
        footerButtonText="Send Invite"
        onFooterButtonClick={handleSave}
        loading={inviteLoading}
        headerVariant="subPage"
        minHeight="auto"
        isNested
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ mb: 1.25, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px' }}>
            Email Address
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter email..."
            value={inviteEmail}
            onChange={(e) => {
              setInviteEmail(e.target.value)
              setInviteError('')
            }}
            error={!!inviteError}
            helperText={inviteError}
            sx={{
              '& .MuiFormHelperText-root': {
                fontFamily: 'InterSemiBold',
                fontSize: '14px',
                lineHeight: '17px',
                fontWeight: 400,
                color: 'rgb(255, 61, 0) !important',
              },
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                '&:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                '&:hover:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                '&.Mui-focused fieldset': { borderColor: '#9135E0' },
              },
              '& .MuiInputBase-input': {
                py: '12px',
                fontFamily: 'InterRegular',
                fontSize: '14px',
                lineHeight: '21px',
                fontWeight: 400,
                color: '#000000',
                '&::placeholder': {
                  fontFamily: 'InterRegular',
                  fontSize: '14px',
                  lineHeight: '21px',
                  fontWeight: 400,
                  color: 'rgb(107, 114, 128)',
                  opacity: 1,
                },
              },
            }}
          />
        </Box>
      </OnboardingTaskModal>

      <OnboardingTaskModal
        open={activeSubModal === 'improveScan'}
        onClose={handleSubModalClose}
        title="Improve Your Scan Results"
        headerVariant="subPage"
        footerButtonText="Save and Continue"
        onFooterButtonClick={handleSave}
        loading={loading}
        footerButtonDisabled={false}
        isNested
        hideCloseIcon={true}
        minHeight="85%"
      >
        <Box sx={{ mt: 2, px: 1 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1.25, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px', lineHeight: '21px' }}>
              Phone Number<span style={{ color: '#9135E0' }}>*</span>
            </Typography>
            <PhoneNumberInput
              context="postSubscription"
              disabled={!!profile?.phones?.[0]?.number}
              value={inputValue}
              onChange={(newValue: string) => {
                setInputValue(newValue)
                // Clear error immediately when typing
                setPhoneError(false)
              }}
              error={phoneError}
              helperText={phoneError ? (!(inputValue || '').trim() ? 'Phone number is required' : 'Invalid phone number') : ''}
              placeholder="Enter phone number here"
            />
          </Box>

          <Box sx={{ my: 3 }}>
            <Typography variant="subtitle2" sx={{ fontFamily: 'InterMedium', fontWeight: 500, color: '#3A155A', fontSize: '16px', mb: 0.5 }}>
              Address 1
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.25, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px', lineHeight: '21px' }}>
              Street Address<span style={{ color: '#9135E0' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              disabled={!!profile?.address?.[0]?.street1}
              value={addressValue.street1}
              onChange={(e) => {
                setAddressValue({ ...addressValue, street1: e.target.value });
                setStreetError(false);
              }}
              placeholder="Enter street address here"
              error={streetError}
              helperText={streetError ? 'Street Address is required' : ''}
              sx={{
                '& .MuiFormHelperText-root': {
                  fontFamily: 'InterSemiBold',
                  fontSize: '14px',
                  lineHeight: '17px',
                  fontWeight: 400,
                  color: 'rgb(255, 61, 0) !important',
                },
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                  '&:hover:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                  '&.Mui-focused fieldset': { borderColor: '#9135E0' },
                  '&.Mui-disabled': {
                    backgroundColor: '#F5F5F5 !important',
                  },
                },
                '& .MuiInputBase-input': {
                  py: '12px',
                  fontFamily: 'InterRegular',
                  fontSize: '14px',
                  lineHeight: '21px',
                  fontWeight: 400,
                  color: '#000000',
                  '&::placeholder': {
                    fontFamily: 'InterRegular',
                    fontSize: '14px',
                    lineHeight: '21px',
                    fontWeight: 400,
                    color: 'rgb(107, 114, 128)',
                    opacity: 1,
                  },
                },
                '& .Mui-disabled': { cursor: 'not-allowed' },
              }}
            />

            <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ mb: 1.25, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px', lineHeight: '21px' }}>State<span style={{ color: '#9135E0' }}>*</span></Typography>
                <StateInput
                  id="primary-state-input"
                  disabled={!!profile?.address?.[0]?.state}
                  paperSx={compactDropdownSx}
                  value={addressValue.state}
                  onChange={(e, newValue) => {
                    const val = newValue.replace(/\s/g, '').toUpperCase().slice(0, 2);
                    setAddressValue({ ...addressValue, state: val });
                    setStateError(false);
                  }}
                  placeholder="Enter state here"
                  error={stateError}
                  helperText={stateError ? 'State is required' : ''}
                  inputSx={{
                    '& .MuiFormHelperText-root': {
                      fontFamily: 'InterSemiBold',
                      fontSize: '14px',
                      lineHeight: '17px',
                      fontWeight: 400,
                      color: 'rgb(255, 61, 0) !important',
                    },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '&:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                      '&:hover:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                      '&.Mui-focused fieldset': { borderColor: '#9135E0' },
                      '&.Mui-disabled': {
                        backgroundColor: '#F5F5F5 !important',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: '12px',
                      fontFamily: 'InterRegular',
                      fontSize: '14px',
                      lineHeight: '21px',
                      fontWeight: 400,
                      color: '#000000',
                      '&::placeholder': {
                        fontFamily: 'InterRegular',
                        fontSize: '14px',
                        lineHeight: '21px',
                        fontWeight: 400,
                        color: 'rgb(107, 114, 128)',
                        opacity: 1,
                      },
                    },
                    '& .Mui-disabled': { cursor: 'not-allowed' },
                  }}
                />
              </Box>
              <Box sx={{ flex: 2 }}>
                <Typography variant="body2" sx={{ mb: 1.25, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px', lineHeight: '21px' }}>City<span style={{ color: '#9135E0' }}>*</span></Typography>
                <CityInput
                  id="primary-city-input"
                  stateCode={addressValue.state}
                  disabled={!!profile?.address?.[0]?.city}
                  paperSx={compactDropdownSx}
                  value={addressValue.city}
                  onChange={(e, newValue) => {
                    const val = newValue.replace(/[^a-zA-Z\s-]/g, '').replace(/^[ -]+/, '').replace(/--+/g, '-').replace(/  +/g, ' ');
                    setAddressValue({ ...addressValue, city: val });
                    setCityError(false);
                  }}
                  placeholder="Enter city here"
                  error={cityError}
                  helperText={cityError ? ((addressValue.city || '').trim().endsWith('-') ? 'City is invalid' : 'City is required') : ''}
                  inputSx={{
                    '& .MuiFormHelperText-root': {
                      fontFamily: 'InterSemiBold',
                      fontSize: '14px',
                      lineHeight: '17px',
                      fontWeight: 400,
                      color: 'rgb(255, 61, 0) !important',
                    },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '&:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                      '&:hover:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                      '&.Mui-focused fieldset': { borderColor: '#9135E0' },
                      '&.Mui-disabled': {
                        backgroundColor: '#F5F5F5 !important',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: '12px',
                      fontFamily: 'InterRegular',
                      fontSize: '14px',
                      lineHeight: '21px',
                      fontWeight: 400,
                      color: '#000000',
                      '&::placeholder': {
                        fontFamily: 'InterRegular',
                        fontSize: '14px',
                        lineHeight: '21px',
                        fontWeight: 400,
                        color: 'rgb(107, 114, 128)',
                        opacity: 1,
                      },
                    },
                    '& .Mui-disabled': { cursor: 'not-allowed' },
                  }}
                />
              </Box>
            </Stack>

            <Box sx={{ mb: 1.5 }}>
              <Typography variant="body2" sx={{ mb: 1.25, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px', lineHeight: '21px' }}>
                Zip Code<span style={{ color: '#9135E0' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                disabled={!!profile?.address?.[0]?.zip}
                value={addressValue.zip}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 5)
                  setAddressValue({ ...addressValue, zip: val })
                  setZipError(false)
                }}
                error={zipError}
                helperText={zipError ? ((addressValue.zip || '').trim() ? 'Zip code must contain 5 digits' : 'Zip code is required') : ''}
                placeholder="Enter zip code here"
                sx={{
                  '& .MuiFormHelperText-root': {
                    fontFamily: 'InterSemiBold',
                    fontSize: '14px',
                    lineHeight: '17px',
                    fontWeight: 400,
                    color: 'rgb(255, 61, 0) !important',
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                    '&:hover:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                    '&.Mui-focused fieldset': { borderColor: '#9135E0' },
                    '&.Mui-disabled': {
                      backgroundColor: '#F5F5F5 !important',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: '12px',
                    fontFamily: 'InterRegular',
                    fontSize: '14px',
                    lineHeight: '21px',
                    fontWeight: 400,
                    color: '#000000',
                    '&::placeholder': {
                      fontFamily: 'InterRegular',
                      fontSize: '14px',
                      lineHeight: '21px',
                      fontWeight: 400,
                      color: 'rgb(107, 114, 128)',
                      opacity: 1,
                    },
                  },
                  '& .Mui-disabled': { cursor: 'not-allowed' },
                }}
              />
            </Box>
          </Box>

          {additionalAddresses.map((addr, idx) => (
            <Box key={idx} sx={{ my: 3.5, position: 'relative' }}>
              <Typography variant="subtitle2" sx={{ fontFamily: 'InterMedium', fontWeight: 500, color: '#3A155A', fontSize: '16px', mb: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                Address {idx + 2}
                <img
                  src={deleteIcon}
                  alt="Delete"
                  onClick={() => handleDeleteAddress(idx)}
                  style={{ cursor: 'pointer', width: 20, height: 20 }}
                />
              </Typography>

              <Typography variant="body2" sx={{ mb: 1.25, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px', lineHeight: '21px' }}>
                Street Address<span style={{ color: '#9135E0' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                disabled={!!profile?.address?.[idx + 1]?.street1}
                value={addr.street1}
                onChange={(e) => {
                  const newList = [...additionalAddresses]
                  newList[idx] = { ...newList[idx], street1: e.target.value }
                  setAdditionalAddresses(newList)
                  if (additionalAddressErrors[idx]) {
                    const newErrors = [...additionalAddressErrors]
                    newErrors[idx] = { ...newErrors[idx], street1: false }
                    setAdditionalAddressErrors(newErrors)
                  }
                }}
                error={!!additionalAddressErrors[idx]?.street1}
                helperText={additionalAddressErrors[idx]?.street1 ? 'Street Address is required' : ''}
                sx={{
                  mb: 2,
                  '& .MuiFormHelperText-root': {
                    fontFamily: 'InterSemiBold',
                    fontSize: '14px',
                    lineHeight: '17px',
                    fontWeight: 400,
                    color: 'rgb(255, 61, 0) !important',
                  },
                  '& .MuiOutlinedInput-root': {
                    paddingLeft: '10px',
                    borderRadius: '12px',
                    bgcolor: '#fff',
                    borderColor: 'rgba(0, 0, 0, 0.2)',
                    '&:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                    '&:hover:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                    '&.Mui-focused fieldset': { borderColor: '#9135E0' },
                    '&.Mui-disabled': {
                      backgroundColor: '#F5F5F5 !important',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: '12px',
                    fontFamily: 'InterRegular',
                    fontSize: '14px',
                    lineHeight: '21px',
                    fontWeight: 400,
                    color: '#000000',
                    '&::placeholder': {
                      fontFamily: 'InterRegular',
                      fontSize: '14px',
                      lineHeight: '21px',
                      fontWeight: 400,
                      color: 'rgb(107, 114, 128)',
                      opacity: 1,
                    },
                  }
                }}
              />

              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1.25, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px', lineHeight: '21px' }}>State<span style={{ color: '#9135E0' }}>*</span></Typography>
                  <StateInput
                    id={`additional-state-input-${idx}`}
                    disabled={!!profile?.address?.[idx + 1]?.state}
                    paperSx={compactDropdownSx}
                    value={addr.state}
                    onChange={(e, newValue) => {
                      const val = newValue.replace(/\s/g, '').toUpperCase().slice(0, 2);
                      const newList = [...additionalAddresses]
                      newList[idx] = { ...newList[idx], state: val }
                      setAdditionalAddresses(newList)
                      if (additionalAddressErrors[idx]) {
                        const newErrors = [...additionalAddressErrors]
                        newErrors[idx] = { ...newErrors[idx], state: false }
                        setAdditionalAddressErrors(newErrors)
                      }
                    }}
                    error={!!additionalAddressErrors[idx]?.state}
                    helperText={additionalAddressErrors[idx]?.state ? 'State is required' : ''}
                    inputSx={{
                      '& .MuiFormHelperText-root': {
                        fontFamily: 'InterSemiBold',
                        fontSize: '14px',
                        lineHeight: '17px',
                        fontWeight: 400,
                        color: 'rgb(255, 61, 0) !important',
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        '&:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                        '&:hover:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                        '&.Mui-focused fieldset': { borderColor: '#9135E0' },
                        '&.Mui-disabled': {
                          backgroundColor: '#F5F5F5 !important',
                        },
                      },
                      '& .MuiInputBase-input': {
                        py: '12px',
                        fontFamily: 'InterRegular',
                        fontSize: '14px',
                        lineHeight: '21px',
                        fontWeight: 400,
                        color: '#000000',
                        '&::placeholder': {
                          fontFamily: 'InterRegular',
                          fontSize: '14px',
                          lineHeight: '21px',
                          fontWeight: 400,
                          color: 'rgb(107, 114, 128)',
                          opacity: 1,
                        },
                      }
                    }}
                  />
                </Box>
                <Box sx={{ flex: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1.25, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px', lineHeight: '21px' }}>City<span style={{ color: '#9135E0' }}>*</span></Typography>
                  <CityInput
                    id={`additional-city-input-${idx}`}
                    stateCode={addr.state}
                    disabled={!!profile?.address?.[idx + 1]?.city}
                    paperSx={compactDropdownSx}
                    value={addr.city}
                    onChange={(e, newValue) => {
                      const newList = [...additionalAddresses]
                      newList[idx] = { ...newList[idx], city: newValue }
                      setAdditionalAddresses(newList)
                      if (additionalAddressErrors[idx]) {
                        const newErrors = [...additionalAddressErrors]
                        newErrors[idx] = { ...newErrors[idx], city: false }
                        setAdditionalAddressErrors(newErrors)
                      }
                    }}
                    error={!!additionalAddressErrors[idx]?.city}
                    helperText={additionalAddressErrors[idx]?.city ? ((addr?.city || '').trim().endsWith('-') ? 'City is invalid' : 'City is required') : ''}
                    inputSx={{
                      '& .MuiFormHelperText-root': {
                        fontFamily: 'InterSemiBold',
                        fontSize: '14px',
                        lineHeight: '17px',
                        fontWeight: 400,
                        color: 'rgb(255, 61, 0) !important',
                      },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                        bgcolor: '#fff',
                        '&:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                        '&:hover:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                        '&.Mui-focused fieldset': { borderColor: '#9135E0' },
                        '&.Mui-disabled': {
                          backgroundColor: '#F5F5F5 !important',
                        },
                      },
                      '& .MuiInputBase-input': {
                        py: '12px',
                        fontFamily: 'InterRegular',
                        fontSize: '14px',
                        lineHeight: '21px',
                        fontWeight: 400,
                        color: '#000000',
                        '&::placeholder': {
                          fontFamily: 'InterRegular',
                          fontSize: '14px',
                          lineHeight: '21px',
                          fontWeight: 400,
                          color: 'rgb(107, 114, 128)',
                          opacity: 1,
                        },
                      }
                    }}
                  />
                </Box>
              </Stack>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 1.25, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px', lineHeight: '21px' }}>Zip Code<span style={{ color: '#9135E0' }}>*</span></Typography>
                <TextField
                  fullWidth
                  disabled={!!profile?.address?.[idx + 1]?.zip}
                  value={addr.zip}
                  onChange={(e) => {
                    const val = (e.target.value || '').replace(/\D/g, '').slice(0, 5)
                    const newList = [...additionalAddresses]
                    newList[idx] = { ...newList[idx], zip: val }
                    setAdditionalAddresses(newList)
                    if (additionalAddressErrors[idx]) {
                      const newErrors = [...additionalAddressErrors]
                      newErrors[idx] = { ...newErrors[idx], zip: false }
                      setAdditionalAddressErrors(newErrors)
                    }
                  }}
                  error={!!additionalAddressErrors[idx]?.zip}
                  helperText={additionalAddressErrors[idx]?.zip ? ((addr?.zip || '').trim() ? 'Zip code must contain 5 digits' : 'Zip code is required') : ''}
                  sx={{
                    '& .MuiFormHelperText-root': {
                      fontFamily: 'InterSemiBold',
                      fontSize: '14px',
                      lineHeight: '17px',
                      fontWeight: 400,
                      color: 'rgb(255, 61, 0) !important',
                    },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '&:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                      '&:hover:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                      '&.Mui-focused fieldset': { borderColor: '#9135E0' },
                      '&.Mui-disabled': {
                        backgroundColor: '#F5F5F5 !important',
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: '12px',
                      fontFamily: 'InterRegular',
                      fontSize: '14px',
                      lineHeight: '21px',
                      fontWeight: 400,
                      color: '#000000',
                      '&::placeholder': {
                        fontFamily: 'InterRegular',
                        fontSize: '14px',
                        lineHeight: '21px',
                        fontWeight: 400,
                        color: 'rgb(107, 114, 128)',
                        opacity: 1,
                      },
                    }
                  }}
                />
              </Box>
            </Box>
          ))}

          <Button
            variant="outlined"
            onClick={() => handleNavigate({ id: 'streetAddress' })}
            startIcon={<AddIcon sx={{ color: '#1a73e8', fontSize: '20px' }} />}
            sx={{
              mt: 1,
              mb: 3.5,
              color: '#1D2333',
              textTransform: 'none',
              fontSize: '16px',
              fontFamily: 'InterMedium', fontWeight: 400,
              bgcolor: '#F3F4F6',
              border: '1px solid #E3E2E9',
              borderRadius: '12px',
              height: '38px',
              width: '221px',
              px: 2,
              py: 1,
              '&:hover': { bgcolor: '#E5E7EB', border: '1px solid #E3E2E9' }
            }}>
            Add Another Address
          </Button>

          <Divider sx={{ my: 3, borderColor: '#E3E2E9' }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontFamily: 'InterMedium', fontWeight: 500, color: '#3A155A', fontSize: '16px', mb: 1 }}>
              Additional Details <Box component="span" sx={{ fontWeight: 400, color: '#3C4252', fontStyle: 'italic' }}>(Optional)</Box>
            </Typography>
            <Box sx={{ mb: 1.25 }}>
              <Typography variant="body2" sx={{ mb: 1.25, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px', lineHeight: '21px' }}>Maiden Name</Typography>
              <TextField
                fullWidth
                disabled={!!profile?.additional_information?.maiden_name?.[0]}
                value={maidenName}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^a-zA-Z\s-]/g, '').replace(/^[ -]+/, '').replace(/--+/g, '-').replace(/\s\s+/g, ' ');
                  setMaidenName(val);
                }}
                onBlur={(e) => {
                  const val = e.target.value;
                  setMaidenNameError(/[ -]+$/.test(val));
                }}
                error={maidenNameError}
                helperText={maidenNameError ? 'Name cannot end with a space or hyphen' : ''}
                placeholder="Enter maiden name"
                sx={{
                  '& .MuiFormHelperText-root': {
                    fontFamily: 'InterSemiBold',
                    fontSize: '14px',
                    lineHeight: '17px',
                    fontWeight: 400,
                    color: 'rgb(255, 61, 0) !important',
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                    '&:hover:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                    '&.Mui-focused fieldset': { borderColor: '#9135E0' },
                    '&.Mui-disabled': {
                      backgroundColor: '#F5F5F5 !important',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: '12px',
                    fontFamily: 'InterRegular',
                    fontSize: '14px',
                    lineHeight: '21px',
                    fontWeight: 400,
                    color: '#000000',
                    '&::placeholder': {
                      fontFamily: 'InterRegular',
                      fontSize: '14px',
                      lineHeight: '21px',
                      fontWeight: 400,
                      color: 'rgb(107, 114, 128)',
                      opacity: 1,
                    },
                  },
                  '& .Mui-disabled': { cursor: 'not-allowed' },
                }}
              />
            </Box>
            <Box sx={{ mb: 1.25 }}>
              <Typography variant="body2" sx={{ mb: 1.25, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px', lineHeight: '21px' }}>Alternative Name</Typography>
              <TextField
                fullWidth
                disabled={!!profile?.additional_information?.additional_first_name?.[0]}
                value={altName}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^a-zA-Z\s-]/g, '').replace(/^[ -]+/, '').replace(/--+/g, '-').replace(/\s\s+/g, ' ');
                  setAltName(val);
                }}
                onBlur={(e) => {
                  const val = e.target.value;
                  setAltNameError(/[ -]+$/.test(val));
                }}
                error={altNameError}
                helperText={altNameError ? 'Name cannot end with a space or hyphen' : ''}
                placeholder="Enter alternative name"
                sx={{
                  '& .MuiFormHelperText-root': {
                    fontFamily: 'InterSemiBold',
                    fontSize: '14px',
                    lineHeight: '17px',
                    fontWeight: 400,
                    color: 'rgb(255, 61, 0) !important',
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                    '&:hover:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                    '&.Mui-focused fieldset': { borderColor: '#9135E0' },
                    '&.Mui-disabled': {
                      backgroundColor: '#F5F5F5 !important',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: '12px',
                    fontFamily: 'InterRegular',
                    fontSize: '14px',
                    lineHeight: '21px',
                    fontWeight: 400,
                    color: '#000000',
                    '&::placeholder': {
                      fontFamily: 'InterRegular',
                      fontSize: '14px',
                      lineHeight: '21px',
                      fontWeight: 400,
                      color: 'rgb(107, 114, 128)',
                      opacity: 1,
                    },
                  },
                  '& .Mui-disabled': { cursor: 'not-allowed' },
                }}
              />
            </Box>

            <Box sx={{ mb: 2.5, mt: 5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Divider sx={{ flex: 1, borderColor: '#E3E2E9' }} />
              <Typography variant="body2" sx={{ px: 2, color: '#3C4252', fontStyle: 'italic', fontSize: '14px', fontFamily: 'InterMedium', fontWeight: 400 }}>
                More Details = Better Results
              </Typography>
              <Divider sx={{ flex: 1, borderColor: '#E3E2E9' }} />
            </Box>
          </Box>
        </Box>
      </OnboardingTaskModal>

      <OnboardingTaskModal
        open={activeSubModal === 'removalTasks'}
        onClose={handleSubModalClose}
        title="Complete Removal Tasks"
        subtitle="Complete these steps to begin your data removal process"
        completedCount={completedRemovalSteps}
        totalSteps={totalRemovalSteps}
        progress={removalProgress}
        steps={removalSubSteps}
        renderStep={renderRemovalStep}
        footerButtonText="Get Started"
        onFooterButtonClick={handleSubModalClose}
        isNested
      />

      <TodoMenu
        item={TODO_ITEMS.find(i => i.id === (activeTodoItemId !== null ? activeTodoItemId : lastActiveTodoItemIdRef.current)) || TODO_ITEMS[0]}
        anchorEl={todoAnchorEl}
        isOpen={Boolean(todoAnchorEl)}
        isCompleted={false}
        menuWidth={menuWidth}
        onClose={handleCloseTodoMenu}
        onConfirm={() => {
          if (activeTodoItemId === 1) {
            dispatch(triggerTodoListAction({ action: 'confirmator', broker_name: 'peoplefinders', action_category: 'broker' }))
          } else if (activeTodoItemId === 2) {
            dispatch(triggerTodoListAction({ action: 'confirmator', broker_name: 'intelius', action_category: 'broker' }))
          } else if (activeTodoItemId === 3) {
            dispatch(triggerTodoListAction({ action: 'acknowledge', action_category: 'data_comm_acknowledgement' }))
          }
        }}
        onTriggerOptout={() => {
          if (activeTodoItemId === 1) {
            dispatch(triggerTodoListAction({ action: 'removal', broker_name: 'peoplefinders', action_category: 'broker' }))
          }
        }}
        onLater={() => {
          if (activeTodoItemId === 1) {
            dispatch(triggerTodoListAction({ action_category: 'broker', action: 'later', broker_name: 'peoplefinders' }))
          } else if (activeTodoItemId === 2) {
            dispatch(triggerTodoListAction({ action_category: 'broker', action: 'later', broker_name: 'intelius' }))
          } else if (activeTodoItemId === 3) {
            dispatch(triggerTodoListAction({ action_category: 'data_comm_acknowledgement', action: 'later' }))
          }
        }}
        zIndex={1500}
      />

      {/* Detail-entry sub-modals (Middle Name, Address, Phone) */}
      <OnboardingTaskModal
        open={activeSubModal === 'streetAddress' || activeSubModal === 'additionalAddresses'}
        onClose={activeSubModal === 'streetAddress' ? handleBackToImproveScan : handleSubModalClose}
        title="Add Another Address"
        headerVariant="subPage"
        footerButtonText="Save Address"
        onFooterButtonClick={handleSave}
        footerButtonDisabled={false}
        loading={loading}
        isNested
      >
        <Stack spacing={3} sx={{ mt: 2 }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1.25, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px' }}>
              Street Address<span style={{ color: '#9135E0' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              value={newAddressValue.street1}
              onChange={(e) => {
                setNewAddressValue({ ...newAddressValue, street1: e.target.value })
                setNewStreetError(false)
              }}
              error={newStreetError}
              helperText={newStreetError ? 'Street Address is required' : ''}
              placeholder="Enter street address here"
              sx={{
                '& .MuiFormHelperText-root': {
                  fontFamily: 'InterSemiBold',
                  fontSize: '14px',
                  lineHeight: '17px',
                  fontWeight: 400,
                  color: 'rgb(255, 61, 0) !important',
                },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                  '&:hover:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                  '&.Mui-focused fieldset': { borderColor: '#9135E0' },
                },
                '& .MuiInputBase-input': {
                  py: '12px',
                  fontFamily: 'InterRegular',
                  fontSize: '14px',
                  lineHeight: '21px',
                  fontWeight: 400,
                  color: '#000000',
                  '&::placeholder': {
                    fontFamily: 'InterRegular',
                    fontSize: '14px',
                    lineHeight: '21px',
                    fontWeight: 400,
                    color: 'rgb(107, 114, 128)',
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>

          <Stack direction="row" spacing={2} sx={{ mt: 1, mb: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" sx={{ mb: 1.25, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px', lineHeight: '21px' }}>
                State<span style={{ color: '#9135E0' }}>*</span>
              </Typography>
              <StateInput
                id="new-state-input"
                paperSx={compactDropdownSx}
                value={newAddressValue.state}
                onChange={(e, newValue) => {
                  const val = newValue.replace(/\s/g, '').toUpperCase().slice(0, 2)
                  setNewAddressValue({ ...newAddressValue, state: val })
                  setNewStateError(false)
                }}
                error={newStateError}
                helperText={newStateError ? 'Invalid state code (e.g. NY)' : ''}
                placeholder="Enter state here"
                inputSx={{
                  '& .MuiFormHelperText-root': {
                    fontFamily: 'InterSemiBold',
                    fontSize: '14px',
                    lineHeight: '17px',
                    fontWeight: 400,
                    color: 'rgb(255, 61, 0) !important',
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                    '&:hover:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                    '&.Mui-focused fieldset': { borderColor: '#9135E0' },
                  },
                  '& .MuiInputBase-input': {
                    py: "12px",
                    fontFamily: 'InterRegular',
                    fontSize: '14px',
                    lineHeight: '21px',
                    fontWeight: 400,
                    color: '#000000',
                    '&::placeholder': {
                      fontFamily: 'InterRegular',
                      fontSize: '14px',
                      lineHeight: '21px',
                      fontWeight: 400,
                      color: 'rgb(107, 114, 128)',
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>
            <Box sx={{ flex: 2 }}>
              <Typography variant="body2" sx={{ mb: 1.25, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px' }}>
                City<span style={{ color: '#9135E0' }}>*</span>
              </Typography>
              <CityInput
                id="new-city-input"
                stateCode={newAddressValue.state}
                paperSx={compactDropdownSx}
                value={newAddressValue.city}
                onChange={(e, newValue) => {
                  const val = newValue.replace(/[^a-zA-Z\s-]/g, '').replace(/^[ -]+/, '').replace(/--+/g, '-').replace(/  +/g, ' ');
                  setNewAddressValue({ ...newAddressValue, city: val })
                  setNewCityError(false)
                }}
                error={newCityError}
                helperText={newCityError ? ((newAddressValue.city || '').trim().endsWith('-') ? 'City is invalid' : 'City is required') : ''}
                placeholder="Enter city here"
                inputSx={{
                  '& .MuiFormHelperText-root': {
                    fontFamily: 'InterSemiBold',
                    fontSize: '14px',
                    lineHeight: '17px',
                    fontWeight: 400,
                    color: 'rgb(255, 61, 0) !important',
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                    '&:hover:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                    '&.Mui-focused fieldset': { borderColor: '#9135E0' },
                  },
                  '& .MuiInputBase-input': {
                    py: "12px",
                    fontFamily: 'InterRegular',
                    fontSize: '14px',
                    lineHeight: '21px',
                    fontWeight: 400,
                    color: '#000000',
                    '&::placeholder': {
                      fontFamily: 'InterRegular',
                      fontSize: '14px',
                      lineHeight: '21px',
                      fontWeight: 400,
                      color: 'rgb(107, 114, 128)',
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>
          </Stack>

          <Box>
            <Typography variant="body2" sx={{ mb: 1.25, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px', lineHeight: '21px' }}>
              Zip Code<span style={{ color: '#9135E0' }}>*</span>
            </Typography>
            <TextField
              fullWidth
              value={newAddressValue.zip}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 5)
                setNewAddressValue({ ...newAddressValue, zip: val })
                setNewZipError(false)
              }}
              error={newZipError}
              helperText={newZipError ? (newAddressValue.zip.trim() ? 'Zip code must contain 5 digits' : 'Zip code is required') : ''}
              placeholder="Enter zip code here"
              sx={{
                '& .MuiFormHelperText-root': {
                  fontFamily: 'InterSemiBold',
                  fontSize: '14px',
                  lineHeight: '17px',
                  fontWeight: 400,
                  color: 'rgb(255, 61, 0) !important',
                },
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  '&:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                  '&:hover:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                  '&.Mui-focused fieldset': { borderColor: '#9135E0' },
                },
                '& .MuiInputBase-input': {
                  py: '12px',
                  fontFamily: 'InterRegular',
                  fontSize: '14px',
                  lineHeight: '21px',
                  fontWeight: 400,
                  color: '#000000',
                  '&::placeholder': {
                    fontFamily: 'InterRegular',
                    fontSize: '14px',
                    lineHeight: '21px',
                    fontWeight: 400,
                    color: 'rgb(107, 114, 128)',
                    opacity: 1,
                  },
                },
              }}
            />
          </Box>

          <Box sx={{ mt: '8px !important', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Divider sx={{ flex: 1 }} />
            <Typography variant="body2" sx={{ px: 2, color: '#3C4252', fontStyle: 'italic', fontSize: '14px', fontFamily: 'InterMedium', fontWeight: 400 }}>
              More Addresses = Better Results
            </Typography>
            <Divider sx={{ flex: 1 }} />
          </Box>
        </Stack>
      </OnboardingTaskModal>

      {/* Other entry sub-modals (Middle Name, Phone) */}
      <OnboardingTaskModal
        open={Boolean(activeSubModal && !['removalTasks', 'improveScan', 'streetAddress', 'additionalAddresses', 'addPlusOne'].includes(activeSubModal))}
        onClose={handleSubModalClose}
        title={activeSubModal === 'middleName' ? 'Add Middle Name' : activeSubModal === 'phoneNumber' ? 'Add Phone Number' : (allSteps.find(s => s.id === activeSubModal)?.label || '')}
        headerVariant="subPage"
        completedCount={completedStepsCount}
        totalSteps={totalSteps}
        progress={progress}
        footerButtonText={activeSubModal === 'middleName' ? 'Save Middle Name' : activeSubModal === 'phoneNumber' ? 'Save Phone Number' : 'Save & Continue'}
        onFooterButtonClick={handleSave}
        footerButtonDisabled={
          activeSubModal === 'middleName' ? !isMiddleNameValid :
            activeSubModal === 'phoneNumber' ? !isPhoneValid :
              false
        }
        loading={loading}
        isNested
      >
        <Box sx={{ mt: 2 }}>
          {activeSubModal === 'middleName' && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px' }}>
                Middle Name<span style={{ color: '#9135E0' }}>*</span>
              </Typography>
              <TextField
                fullWidth
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Enter your middle name"
                sx={{
                  mb: 3,
                  '& .MuiFormHelperText-root': {
                    fontFamily: 'InterSemiBold',
                    fontSize: '14px',
                    lineHeight: '17px',
                    fontWeight: 400,
                    color: 'rgb(255, 61, 0) !important',
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                    '&:hover:not(.Mui-disabled) fieldset': { borderColor: '#9135E0' },
                    '&.Mui-focused fieldset': { borderColor: '#9135E0' },
                  },
                  '& .MuiInputBase-input': {
                    py: '12px',
                    fontFamily: 'InterRegular',
                    fontSize: '14px',
                    lineHeight: '21px',
                    fontWeight: 400,
                    color: '#000000',
                    '&::placeholder': {
                      fontFamily: 'InterRegular',
                      fontSize: '14px',
                      lineHeight: '21px',
                      fontWeight: 400,
                      color: 'rgb(107, 114, 128)',
                      opacity: 1,
                    },
                  },
                }}
              />
            </Box>
          )}

          {activeSubModal === 'phoneNumber' && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontFamily: 'InterRegular', fontWeight: 400, color: '#000000', fontSize: '14px' }}>
                Phone Number<span style={{ color: '#9135E0' }}>*</span>
              </Typography>
              <PhoneNumberInput
                context="postSubscription"
                value={inputValue}
                onChange={(newValue: string) => {
                  setInputValue(newValue)
                  setPhoneError(false)
                }}
                error={phoneError}
                helperText={!inputValue.trim() ? 'Phone number is required' : 'Invalid phone number'}
              />
            </Box>
          )}
        </Box>
      </OnboardingTaskModal>

    </>
  )
}

export default MultiStepProfileCompletionModal
